import {
  distanceKilometres,
  walkingMinutes,
  type LocationPoint,
  type Venue,
} from '../planning.ts'
import type { TransitMetadata } from './protocol.ts'
import type { TransitResult } from './router.ts'

export type RankingPreference = 'balanced' | 'fairest' | 'fastest'
export type CandidatePriorityMode = 'walk' | 'bike' | 'taxi'

export interface GroupTravel {
  minutes: number[]
  range: number
  mean: number
  maximum: number
  score: number
}

export interface GroupRankedVenue extends Venue, GroupTravel {}

export interface CandidateStation extends GroupTravel {
  stationIndex: number
  withinMaximum: boolean
}

export function travelMetrics(minutes: number[], preference: RankingPreference): GroupTravel {
  const maximum = Math.max(...minutes)
  const minimum = Math.min(...minutes)
  const mean = minutes.reduce((total, value) => total + value, 0) / minutes.length
  const range = maximum - minimum
  const score = preference === 'fairest'
    ? range * 10_000 + mean
    : preference === 'fastest'
      ? mean * 10_000 + range
      : range + 0.3 * mean + 0.1 * maximum
  return { minutes, range, mean, maximum, score }
}

export function compareTravel(
  left: GroupTravel,
  right: GroupTravel,
  preference: RankingPreference,
): number {
  if (preference === 'fairest') return left.range - right.range || left.mean - right.mean
  if (preference === 'fastest') return left.mean - right.mean || left.range - right.range
  return left.score - right.score
}

export function withinTravelTolerance(
  best: GroupTravel,
  candidate: GroupTravel,
  preference: RankingPreference,
  toleranceMinutes = 5,
): boolean {
  const rankingDifference = preference === 'fairest'
    ? candidate.range - best.range
    : preference === 'fastest'
      ? candidate.mean - best.mean
      : candidate.score - best.score
  return rankingDifference <= toleranceMinutes
    && candidate.minutes.every((minutes, index) => minutes - best.minutes[index] <= toleranceMinutes)
}

export function candidateStations(
  results: TransitResult[],
  preference: RankingPreference,
  maximumMinutes: number | null,
): CandidateStation[] {
  const stationCount = results[0]?.journeys.length ?? 0
  return Array.from({ length: stationCount }, (_, stationIndex) => stationIndex).flatMap((stationIndex) => {
    const journeys = results.map((result) => result.journeys[stationIndex])
    if (journeys.some((journey) => !journey)) return []
    const metrics = travelMetrics(journeys.map((journey) => Math.ceil(journey!.durationSeconds / 60)), preference)
    return [{
      stationIndex,
      ...metrics,
      withinMaximum: maximumMinutes === null || metrics.maximum <= maximumMinutes,
    }]
  }).sort((left, right) => Number(right.withinMaximum) - Number(left.withinMaximum)
    || compareTravel(left, right, preference))
}

export function rankWalkingVenues(
  venues: Venue[],
  origins: LocationPoint[],
  preference: RankingPreference,
  maximumMinutes: number | null,
): GroupRankedVenue[] {
  return venues.flatMap((venue) => {
    const metrics = travelMetrics(origins.map((origin) => walkingMinutes(origin, venue)), preference)
    if (maximumMinutes !== null && metrics.maximum > maximumMinutes) return []
    return [{
      ...venue,
      ...metrics,
    }]
  }).sort((left, right) => compareTravel(left, right, preference))
}

export function rankEstimatedRoadVenues(
  venues: Venue[],
  origins: LocationPoint[],
  modesByOrigin: readonly (readonly CandidatePriorityMode[])[],
  preference: RankingPreference,
): GroupRankedVenue[] {
  return venues.map((venue) => {
    const metrics = travelMetrics(origins.map((origin, index) => {
      const estimates = modesByOrigin[index]?.map((mode) => estimatedRoadMinutes(origin, venue, mode)) ?? []
      return estimates.length ? Math.min(...estimates) : walkingMinutes(origin, venue)
    }), preference)
    return { ...venue, ...metrics }
  }).sort((left, right) => compareTravel(left, right, preference))
}

function estimatedRoadMinutes(
  origin: LocationPoint,
  venue: Venue,
  mode: CandidatePriorityMode,
): number {
  if (mode === 'walk') return walkingMinutes(origin, venue)
  const distanceKm = distanceKilometres(origin, venue)
  if (mode === 'bike') return Math.max(1, Math.ceil((distanceKm * 1.15 / 15) * 60))
  return Math.max(1, Math.ceil((distanceKm * 1.2 / 30) * 60)) + 5
}

export function selectCandidateAreas(
  candidates: CandidateStation[],
  metadata: TransitMetadata,
  maximumAreas = 3,
  separationKm = 0.8,
): CandidateStation[] {
  const selected: CandidateStation[] = []
  for (const candidate of candidates) {
    const station = metadata.stations[candidate.stationIndex]
    if (!station) continue
    const separated = selected.every((existing) => {
      const other = metadata.stations[existing.stationIndex]
      return distanceKilometres(
        { lat: station[2], lon: station[3] },
        { lat: other[2], lon: other[3] },
      ) >= separationKm
    })
    if (separated) selected.push(candidate)
    if (selected.length === maximumAreas) break
  }
  return selected
}
